/* component-bundle-set.js — <bundle-set> (native replacement for the BOGOS
   "Perfektes Komfort-Set" frequently-bought-together widget).
   - Rows: the main product (the buy-box variant picker is the SINGLE source of truth for
     it — see _wireMainSync / _syncPickerFromMain) plus companion products with their own
     variant <select> and qty stepper.
   - Gesamtpreis = sum of CHECKED rows (variant price × qty), all math in cents.
   - ADD: one /cart/add.js call via KK.cart.add(items[]) — companions carry the
     _upsell:'bundle' line-item property (take-rate reporting; hidden in the UI by the
     underscore filter). The CartStore publishes kk:cart:updated with `added`, which
     auto-opens the cart drawer — no drawer code needed here.
   ES module, one component per file, no global pollution beyond window.KK usage. */

class BundleSet extends HTMLElement {
  connectedCallback() {
    this.totalEl = this.querySelector('[data-bundle-total]');
    this.btn = this.querySelector('[data-bundle-add]');
    this.errEl = this.querySelector('[data-bundle-error]');

    this.addEventListener('change', (e) => {
      // The main row's size select is a VIEW of the buy-box picker (buy-path audit
      // 2026-07-31): a user change here drives the picker, which republishes
      // kk:variant:changed and rewrites every surface (incl. this row).
      const mainSel = e.target && e.target.closest && e.target.closest('[data-bundle-main-variant]');
      if (mainSel) this._syncPickerFromMain(mainSel);
      this._recalc();
    });
    this.addEventListener('click', (e) => {
      const step = e.target.closest('[data-bundle-step]');
      if (step) { this._step(step); return; }
      if (e.target.closest('[data-bundle-add]')) { e.preventDefault(); this._add(); }
    });

    this._wireMainSync();
    this._recalc();
  }

  /* Keep the "Dieser Artikel" row in lockstep with the buy-box variant picker.
     ONE source of truth (buy-path audit 2026-07-31): the picker owns the main variant.
     Its broadcast rewrites this row's data-variant-id/data-price (which is all the
     set-add reads) and mirrors the value into the row's own <select>. The reverse
     direction lives in _syncPickerFromMain. */
  _wireMainSync() {
    const wire = () => {
      if (!(window.KK && window.KK.pubsub)) return false;
      window.KK.pubsub.subscribe('kk:variant:changed', (p) => {
        if (!p || p.sectionId !== this.getAttribute('data-section') || !p.variant) return;
        const row = this.querySelector('[data-bundle-row][data-main]');
        if (!row) return;
        row.setAttribute('data-variant-id', String(p.variant.id));
        if (typeof p.variant.price === 'number') row.setAttribute('data-price', String(p.variant.price));
        const sel = row.querySelector('[data-bundle-main-variant]');
        // Programmatic value write — fires no change event, so no feedback loop.
        if (sel) {
          let opt = sel.querySelector('option[value="' + p.variant.id + '"]');
          if (!opt) {
            /* The select only renders AVAILABLE variants, so a picker landing on a
               sold-out one used to leave it showing the PREVIOUS size — a lying label
               is exactly the divergence this audit fixed (2026-07-31). Mirror the
               picker with a placeholder instead. */
            opt = document.createElement('option');
            opt.value = String(p.variant.id);
            opt.textContent = (p.variant.options || []).join(' / ');
            opt.setAttribute('data-price', String(p.variant.price));
            opt.setAttribute('data-kk-mirror', '');
            sel.appendChild(opt);
          }
          sel.value = String(p.variant.id);
          sel.querySelectorAll('option[data-kk-mirror]').forEach((o) => { if (o !== opt) o.remove(); });
        }
        const price = row.querySelector('[data-bundle-row-price]');
        if (price && typeof p.variant.price === 'number') price.textContent = this._money(p.variant.price);
        this._recalc();
      });
      return true;
    };
    if (!wire()) document.addEventListener('DOMContentLoaded', wire, { once: true });
  }

  /* Bundle main-row select -> variant picker (buy-path audit 2026-07-31, CRITICAL).
     Before: this select was a SECOND source of truth. Measured on the premium topper PDP —
     main form held 54589165240588 (189,95 €) while the set CTA added 54589164978444
     (78,95 €) for the SAME product, and the buy box still showed the other price.
     Now: the select drives the picker (option values written, then ONE synthetic change
     so the picker's own handler resolves the variant, rewrites the form's hidden
     name="id" + ?variant= and republishes kk:variant:changed). Programmatic .checked /
     .value writes fire no 'change', so this cannot loop. */
  _syncPickerFromMain(sel) {
    const row = sel.closest('[data-bundle-row]');
    const id = Number(sel.value);
    if (!row || !id) return;

    // Keep our own state consistent even when there is no picker to drive
    // (single-variant products, or a template without the variant_picker block).
    const opt = sel.selectedOptions && sel.selectedOptions[0];
    const price = opt ? Number(opt.getAttribute('data-price')) : NaN;
    row.setAttribute('data-variant-id', String(id));
    if (Number.isFinite(price)) row.setAttribute('data-price', String(price));

    const picker = document.querySelector('variant-picker[data-section="' + this.getAttribute('data-section') + '"]');
    if (!picker) return;
    const variant = this._pickerVariant(picker, id);
    if (!variant || !variant.options) return;

    let touched = null;
    variant.options.forEach((value, i) => {
      const pos = i + 1;
      picker.querySelectorAll('input[type="radio"][data-option-position="' + pos + '"]').forEach((radio) => {
        const on = radio.value === value;
        if (on && !radio.checked) touched = radio;
        radio.checked = on;
      });
      const optSel = picker.querySelector('select[data-option-position="' + pos + '"]');
      if (optSel && optSel.value !== value) { optSel.value = value; touched = optSel; }
    });
    const ctl = touched || picker.querySelector('[data-option-position]');
    if (ctl) ctl.dispatchEvent(new Event('change', { bubbles: true }));
  }

  // The picker's minimal variant map (instance property once upgraded, embedded JSON before).
  _pickerVariant(picker, id) {
    let list = picker.variants;
    if (!Array.isArray(list)) {
      const json = picker.querySelector('[data-variant-json]');
      try { list = json ? JSON.parse(json.textContent) : []; } catch (e) { list = []; }
    }
    return list.find((v) => String(v.id) === String(id)) || null;
  }

  _rows() {
    return [...this.querySelectorAll('[data-bundle-row]')].map((row) => {
      const check = row.querySelector('[data-bundle-check]');
      const sel = row.querySelector('[data-bundle-variant]');
      const qtyEl = row.querySelector('[data-bundle-qty]');
      const qty = Math.max(1, parseInt(qtyEl ? qtyEl.textContent : '1', 10) || 1);
      const main = row.hasAttribute('data-main');
      /* MAIN row: read the row attributes, never the <select> (buy-path audit 2026-07-31).
         The picker is the single source of truth and _wireMainSync/_syncPickerFromMain keep
         these attributes on it; the select is only a view. This also fixes a latent drop —
         the select only renders AVAILABLE variants, so a picker variant missing from it left
         sel.value === '' and the main product fell out of the set-add entirely. */
      const useSel = sel && !main;
      const id = useSel ? Number(sel.value) : Number(row.getAttribute('data-variant-id'));
      const price = useSel
        ? Number(sel.selectedOptions[0] && sel.selectedOptions[0].getAttribute('data-price'))
        : Number(row.getAttribute('data-price'));
      return { row: row, checked: !check || check.checked, id: id, qty: qty, price: price, main: main };
    });
  }

  _money(cents) {
    return (cents / 100).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
  }

  _recalc() {
    let total = 0;
    let any = false;
    for (const r of this._rows()) {
      // keep every row's price label in sync with its select (main row included —
      // the user can change the main size directly in the set now)
      const label = r.row.querySelector('[data-bundle-row-price]');
      if (label && Number.isFinite(r.price)) label.textContent = this._money(r.price);
      if (r.checked && Number.isFinite(r.price)) { total += r.price * r.qty; any = true; }
    }
    if (this.totalEl) this.totalEl.textContent = this._money(total);
    // _recalc owns the button's disabled state, so it must not un-disable an in-flight
    // add (contract rule 5: the guard and the busy state stay symmetric).
    if (this.btn) this.btn.disabled = !any || !!this._pending;
  }

  _step(btn) {
    const row = btn.closest('[data-bundle-row]');
    const qtyEl = row && row.querySelector('[data-bundle-qty]');
    if (!qtyEl) return;
    const next = Math.max(1, (parseInt(qtyEl.textContent, 10) || 1) + (btn.getAttribute('data-bundle-step') === '+' ? 1 : -1));
    qtyEl.textContent = String(next);
    this._recalc();
  }

  _add() {
    if (!(window.KK && window.KK.cart) || this._pending) return;
    // Guard symmetry (contract rule 5): we set BOTH disabled and aria-busy below, so the
    // guard reads BOTH — a double tap can never fire a second multi-add.
    if (this.btn && (this.btn.disabled || this.btn.getAttribute('aria-busy') === 'true')) return;
    const rows = this._rows().filter((r) => r.checked && r.id);
    const items = rows.map((r) => {
      const item = { id: r.id, quantity: r.qty };
      if (!r.main) item.properties = { _upsell: 'bundle' };
      return item;
    });
    if (!items.length) return;
    this._pending = true;
    this._hideError();
    this.btn.disabled = true;
    this.btn.setAttribute('aria-busy', 'true');
    this.btn.classList.add('is-loading');
    if (!this.btn.hasAttribute('data-kk-prev')) this.btn.setAttribute('data-kk-prev', this.btn.textContent);
    if (this.btn.getAttribute('data-adding-text')) this.btn.textContent = this.btn.getAttribute('data-adding-text');
    window.KK.cart.add(items, { sections: this._sectionsToRender() })
      .then(() => {
        if (window.KK.track) {
          window.KK.track('kk_upsell_bundle_add', {
            item_count: items.length,
            companion_count: items.filter((i) => i.properties).length,
          });
        }
      })
      // Never swallow (contract rule 6): the set add is ONE atomic /cart/add.js, so a
      // single out-of-stock companion used to kill the whole add — main product included —
      // with zero feedback. Buyer saw "nothing happened" and tapped again.
      .catch((err) => this._showError(err, rows))
      // Contract rule 4: the busy state is cleared HERE, never only in .catch().
      .finally(() => {
        this._pending = false;
        this.btn.removeAttribute('aria-busy');
        this.btn.classList.remove('is-loading');
        if (this.btn.hasAttribute('data-kk-prev')) { this.btn.textContent = this.btn.getAttribute('data-kk-prev'); this.btn.removeAttribute('data-kk-prev'); }
        this._recalc(); // restores the derived disabled state (no rows checked => disabled)
      });
  }

  _sectionsToRender() {
    const ids = [];
    if (document.getElementById('cart-drawer')) ids.push('cart-drawer');
    ids.push('header');
    return ids.join(',');
  }

  _hideError() { if (this.errEl) this.errEl.hidden = true; }

  /* Buyer-facing failure copy. CartStore contract: render err.userMessage ONLY — never
     err.message / raw exception text. Fallback is the localized string the section renders
     into data-error-fallback (cart.error_add). /cart/add.js with an items[] array is ATOMIC,
     so on rejection NOTHING was added; naming the items makes that comprehensible. */
  _showError(err, rows) {
    const msg = (err && err.userMessage) || this.getAttribute('data-error-fallback') || '';
    const names = (rows || []).map((r) => r.row.getAttribute('data-bundle-title')).filter(Boolean);
    const text = (msg + (names.length ? ' Nicht hinzugefügt: ' + names.join(', ') + '.' : '')).trim();
    if (this.errEl && text) {
      this.errEl.textContent = text;
      this.errEl.hidden = false;
    }
    if (window.console && console.warn) {
      console.warn('[bundle-set] add failed', (err && err.code) || 'unknown');
    }
  }
}

if (!customElements.get('bundle-set')) {
  customElements.define('bundle-set', BundleSet);
}
